import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthentificationService } from '../authentification/authentification.service';
import { AlertesService } from '../shared/alertes.service';
import {
  boutonSnackBar,
  dureeSnackBarError,
  dureeSnackBarSuccess,
  questionnaireAidesApportees, questionnaireBAV,
  questionnaireContexteFP,
  questionnaireCore, questionnaireDG, questionnaireMoyens,
  questionnairePersonnesRecues,
  questionnairePremiersContacts,
  questionnaireSyntheseBAV,
  validationsQuestionsDynamiques,
  variableIdentifiante
} from '../shared/questionnaire.metadata';
import { ValidationService } from '../shared/validation/validation.service';
import { SaisieService } from './saisie.service';

@Injectable({
  providedIn: 'root'
})
export class FormulaireService {
  private currentRootForm: UntypedFormGroup;
  private subModifLocalStorage: Subscription;
  public modificationEnCours: boolean = false;

  /**
  *? Il est possible de garder un historique des formulaires en cours de saisie au cas ou … voir si utile
  */
  // private listeRootForm: Map<string, FormGroup>;
  constructor(
    private saisieService: SaisieService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private validationService: ValidationService,
    private alertesService: AlertesService,
    private router: Router,
    private authentificationService: AuthentificationService,
  ) {
  }

  /**
   * Fonction permettant de récupérer un questionnaire déjà saisie et enregistrer
   * ! Se rend sur /saisie à la fin du chargement
   * @param valeurIdentifiant valeur de la variable identifiant un questionnaire
   */
  private chargementQuestionnaireExistant(valeurIdentifiant: string[]): void {
    this.currentRootForm = this.creerQuestionnaire();
    this.validationService.ajoutValidationCroisee(this.currentRootForm);
    this.alertesService.ajoutAlertes(this.currentRootForm);
    this.saisieService.getSaisieJava$(valeurIdentifiant).subscribe(saisie => {
      // Gestion d’un message retour
      let messageSnackbar = '';
      if (saisie === null || (saisie.ko && saisie.ko === true)) {
        this.currentRootForm.get('ko').setValue(true);
      } else {
            // Cération du questionnaire et préparation des contrôles croisés ainsi que des alertes
            // TODO juste pour le serveur json, à supprimer dès que le back-end est dispo
            // Sinon on se retrouver avec un objet undefined lors du chargement d’une saisie
            if (saisie.length > 0) {
              saisie = saisie[0];
            }
            // pour enquete permanente, mettre daCreation et utilisateurCreation!
            if (Object.keys(saisie).length !== 0 && (saisie.daStk !== null)) {
               messageSnackbar = 'Récupération de la saisie de la juridiction : ' + saisie.ielst
                + ' commencée le ' + moment(saisie.daCreation).format('DD/MM/YYYY') + ' (' + saisie.utilisateurCreation + ')';
              // OU ci-dessous si enquete ponctuelle
              //messageSnackbar = 'Récupération de la saisie ' + saisie.noCmpst
              //  + ' commencée le ' + moment(saisie.daStk).format('DD/MM/YYYY') + ' (' + saisie.mailQuestionnaire + ')';
            } else {
              messageSnackbar = 'Génération d’une nouvelle saisie';
            }
            this.snackBar.open(messageSnackbar, boutonSnackBar,
              { duration: dureeSnackBarSuccess, panelClass: ['snackbar', 'snackbar-success'] });

            // Remplissage du questionnaire via les données reçues
            this.currentRootForm.patchValue(saisie);
            // Traitement des questions « dynamiques »
            this.chargementDesQuestionsDynamiques(saisie, this.currentRootForm);
            // On remet bien la valeur identifiante pour sécurisé l’ensemble, surtout dans le cas d’erreur 404
            // où le retour est une liste vide !
            let index = 0;
            variableIdentifiante.forEach((nomVariable) => {
              this.currentRootForm.get(nomVariable).patchValue(valeurIdentifiant[index]);
              index++;
            });
            // console.log('fin rechargement', this.currentRootForm);

            // Nouveau questionnaire donc aucune modification pour l’instant
            this.surveillerNouveaute();
            // On navigue vers /saisie pour finaliser le chargement
            this.router.navigate(['/saisie']);
      }
    },
    (error) => {
      this.currentRootForm.get('ko').setValue(true);
    });
  }
  /**
   * Permet de remplir dans le FormGroup les questions utilisant des FormArray, généralement des questions
   * que l’on gère dynamiquement
   * @param saisie Retour d’une saisie provenant du backend java
   */
  private chargementDesQuestionsDynamiques(saisie: any, formulaire: UntypedFormGroup) {
    Object.keys(saisie).forEach(valeur => {
      if (valeur && saisie[valeur] && typeof (saisie[valeur]) === 'object') {
        // On ne traite que les tableaux JSON
        if (saisie[valeur] instanceof Array) {
          // Gestion du cas où le tableau contient des objets simples (select multiple)
          let pasQueNombre = false;
          saisie[valeur].forEach(item => {
            if (typeof item !== 'number') {
              pasQueNombre = true;
            }
          });
          // Cas où le tableau contient des objets complexes
          if (pasQueNombre && saisie[valeur].length > 0) {
            // Gestion des questions dynamiques
            const formArray = new UntypedFormArray([]);
            // On vérifie qu’il y a des données dedans
            if (Object.keys(saisie[valeur]).length !== 0) {
              Object.keys(saisie[valeur]).forEach((indexQuestion) => {
                const formGroup = new UntypedFormGroup({});
                Object.keys(saisie[valeur][indexQuestion]).forEach((nomVariable) => {
                  formGroup.setControl(nomVariable, new UntypedFormControl(saisie[valeur][indexQuestion][nomVariable],
                    validationsQuestionsDynamiques.get(nomVariable)));
                });
                // Le nœud que l’on est en train de parcourir peut lui même contenir des FormArry
                this.chargementDesQuestionsDynamiques(saisie[valeur][indexQuestion], formGroup);

                formArray.push(formGroup);

              });
            }
            formulaire.setControl(valeur, formArray);
          }
        } else {
          if (formulaire) {
            this.chargementDesQuestionsDynamiques(saisie[valeur], formulaire.get(valeur) as UntypedFormGroup);
          } else {
            console.error('Formulaire introuvable !');
          }
        }
      }
    });
  }

  /**
   * Effacer le questionnaire
   */
  public effacerQuestionnaire() {
    // Vérification si le questionnaire est nul avant tout
    if (this.currentRootForm) {
      this.currentRootForm = null;
    }
    // Surveillance des modifications à arrêter
    if (this.subModifLocalStorage && ! this.subModifLocalStorage.closed) {
      this.subModifLocalStorage.unsubscribe();
    }
    this.modificationEnCours = false;
  }


  /**
   * Récupération d’un questionnaire à partir d’une adresse email
   * @param valeurIdentifiant La valeur de la variable identifiant un questionnaire.
   *   Peut-être une adresse mail du questionnaire à récupérer (facultatif), un ielst etc
   */
  public getRootFormByVariable(valeurIdentifiant: string[]): UntypedFormGroup {
    /* Variables nécessaires pour parcourir les listes des valeurIdentifiantes */
    let nouvelleValeurIdentifiant = false;
    let index = 0;
    if (this.currentRootForm && variableIdentifiante) {
      variableIdentifiante.forEach((nomVariable) => {
        /* Vérification si le formulaire en cours correspond aux valeurs indentifiantes demandées */
        nouvelleValeurIdentifiant = nouvelleValeurIdentifiant
        || this.currentRootForm.get(nomVariable).value !== valeurIdentifiant[index];
        index++;
      });
    }
    // Si le questionnaire n’existe pas dans l’application, on le récupère
    if (!this.currentRootForm || nouvelleValeurIdentifiant) {
      // this.logger.log('Génération d’un nouveau questionnaire pour ' + valeurIdentifiant, 'FormulaireService');
      // On joint un WS et si pas de questionnaire, on en crée un nouveau
      this.chargementQuestionnaireExistant(valeurIdentifiant);
    }
    return this.currentRootForm;
  }
  public getRootForm(): UntypedFormGroup {
    return this.currentRootForm;
  }
  public supprimer(): Observable<boolean> {
    // Variable retourner pour savoir si l’avancement de la suppression
    const result = new BehaviorSubject(null);
    if (this.currentRootForm.get('id').value) {

      this.saisieService.delSaisie$(this.valeursIdentifiantsSaisieEnCours).subscribe(() => {
        this.snackBar.open('La saisie a bien été supprimée.', boutonSnackBar,
          { duration: dureeSnackBarSuccess, panelClass: ['snackbar', 'snackbar-success'] });
        this.currentRootForm = null;
        result.next(true);
      }, error => {
        this.snackBar.open(`Une erreur est survenue lors de la suppression de la saisie
             (${error.message || 'Problème serveur ; contacter la maintenance.'})`,
          boutonSnackBar, { duration: dureeSnackBarError, panelClass: ['snackbar', 'snackbar-error'] });
        result.next(false);
      }, () => { result.complete(); });
    } else {
      // La saisie ne provient pas du backend java. On ne peut donc pas l’y supprimer …
      this.currentRootForm = null;
      result.next(true);
      // TODO potentiellement gếnant de ne pas le fermer. À voir.
    }
    return result.asObservable();
  }

  /**
   * sauvegarder le questionnaire sans controles
   */
  public sauvegarder(): Observable<boolean> {

    const valid: boolean = false;
    // Variable retournee pour savoir si l’avancement de l’enregistrement
    const result = new BehaviorSubject(null);
    const saisie = this.saisieService.formulaireToSaisie(this.getRootForm());
    if (saisie['id']) {
      // Dans ce cas, l’utilisateur existe déjà en base
      this.saisieService.putSaisie$(saisie, valid).subscribe(() => {
        this.snackBar.open(`Le questionnaire a bien été sauvegardé en l'état.`, boutonSnackBar,
          { duration: dureeSnackBarSuccess, panelClass: ['snackbar', 'snackbar-success'] });
        result.next(true);
        this.surveillerNouveaute();
      },
        error => {
          this.snackBar.open(`Une erreur est survenue pendant la sauvegarde du questionnaire
             (${error.message || 'Problème serveur ; contacter la maintenance.'})`,
            boutonSnackBar, { duration: dureeSnackBarError, panelClass: ['snackbar', 'snackbar-error'] });
          result.next(false);
        }, () => { result.complete(); });
    } else {
      // Cas d’une nouvelle saisie
      this.saisieService.postSaisie$(saisie, valid).subscribe((id) => {
        // console.log('id', id);
        this.getRootForm().setControl('id', new UntypedFormControl(id));
        this.snackBar.open(`Le questionnaire a bien été sauvegardé en l'état.`, boutonSnackBar,
          { duration: dureeSnackBarSuccess, panelClass: ['snackbar', 'snackbar-success'] });
        result.next(true);
        this.surveillerNouveaute();
      },
        error => {
          this.snackBar.open(`Une erreur est survenue pendant la sauvegarde du questionnaire
             (${error.message || 'Problème serveur ; contacter la maintenance.'})`,
            boutonSnackBar, { duration: dureeSnackBarError, panelClass: ['snackbar', 'snackbar-error'] });
          result.next(false);
        }, () => { result.complete(); });
    }
    return result.asObservable();
  }

  /**
   * Envoyer le questionnaire
   */
  public envoyer(): Observable<boolean> {
    const valid: boolean = true;
    // Variable retourner pour savoir si l’avancement de l’enregistrement
    const result = new BehaviorSubject(null);
    const saisie = this.saisieService.formulaireToSaisie(this.getRootForm());
    if (saisie['id']) {
      // Dans ce cas, l’utilisateur existe déjà en base
      this.saisieService.putSaisie$(saisie, valid).subscribe(() => {
        result.next(true);
      },
        error => {
          this.snackBar.open(`Une erreur est survenue pendant l'enregistrement de la saisie
             (${error.message || 'Problème serveur ; contacter la maintenance.'})`,
            boutonSnackBar, { duration: dureeSnackBarError, panelClass: ['snackbar', 'snackbar-error'] });
          result.next(false);
        }, () => { result.complete(); });
    } else {
      // Cas d’une nouvelle saisie
      this.saisieService.postSaisie$(saisie, valid).subscribe(() => {
        result.next(true);
      },
        error => {
          this.snackBar.open(`Une erreur est survenue pendant l'enregistrement de la saisie
             (${error.message || 'Problème serveur ; contacter la maintenance.'})`,
            boutonSnackBar, { duration: dureeSnackBarError, panelClass: ['snackbar', 'snackbar-error'] });
          result.next(false);
        }, () => { result.complete(); });
    }
    return result.asObservable();

  }

  private creerQuestionnaire(): UntypedFormGroup {
    const form: UntypedFormGroup = this.formBuilder.group(questionnaireCore);
    const dg: UntypedFormGroup = this.formBuilder.group(questionnaireDG);
    const moyens: UntypedFormGroup = this.formBuilder.group(questionnaireMoyens);
    const premiersContacts: UntypedFormGroup = this.formBuilder.group(questionnairePremiersContacts);
    const personnesRecues: UntypedFormGroup = this.formBuilder.group(questionnairePersonnesRecues);
    const contexteFaitsProcedure: UntypedFormGroup = this.formBuilder.group(questionnaireContexteFP);
    const aidesApportees: UntypedFormGroup = this.formBuilder.group(questionnaireAidesApportees);
    const bav: UntypedFormGroup = this.formBuilder.group(questionnaireBAV);
    const syntheseBav: UntypedFormGroup = this.formBuilder.group(questionnaireSyntheseBAV);
    
    form.addControl('dg', dg);
    form.addControl('moyens', moyens);
    form.addControl('premiersContacts', premiersContacts);
    form.addControl('personnesRecues', personnesRecues);
    form.addControl('contexteFaitsProcedure', contexteFaitsProcedure);
    form.addControl('aidesApportees', aidesApportees);
    form.addControl('bav', bav);
    form.addControl('syntheseBav', syntheseBav);
    
    form.addControl('ko', new UntypedFormControl(false));

    // Récupération de données utiles mais n’apparaissant pas à l’écran

    //form.get('mailQuestionnaire').patchValue(this.authentificationService.getEmail());
    // Map pour envoyer les alertes a l'api si besoin
    form.addControl('listeAlertesForm', new UntypedFormControl());
    
    return form;
  }
  /**
   * Fonction permettant de surveiller la survenue d’une modification qui n’a pas été sauvegardée en local ou sur le serveur
   * Va modifier la valeur de la variable modificationEnCours
   */
  private surveillerNouveaute() {
    if (this.currentRootForm) {
      if (this.subModifLocalStorage && !this.subModifLocalStorage.closed) {
        this.subModifLocalStorage.unsubscribe();
      }
      this.modificationEnCours = false;
      setTimeout(() => {
        // Il peut arriver que l’utilisateur quitte le questionnaire après l’avoir ouvert.
        // Dans ce cas currentRootForm n’existe plus …
        if (this.currentRootForm) {
          this.subModifLocalStorage = this.currentRootForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
            this.modificationEnCours = true;
          });
        }
      }, 5000);
    }
  }
  /** retourne un tableau contenant les valeurs identifiantes de la saisie en cours */
  private get valeursIdentifiantsSaisieEnCours(): string[] {
    return this.valeursIdentifiantsSaisie(this.currentRootForm.value);
  }
  /** retourne un tableau contenant les valeurs identifiantes d’une saisie en paramètre */
  private valeursIdentifiantsSaisie(saisie: any): string[] {
    const identifiants: string[] = [];
    variableIdentifiante.forEach((nomVariable) => {
      identifiants.push(saisie[nomVariable]);
    });
    return identifiants;
  }

}
